import React, { FC, useMemo, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { SearchIcon } from '@modernloop/shared/icons';
import { Stack, TextField } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { InterviewModuleMemberState, TrainingStatus } from 'src/generated/mloop-graphql';

import FilterDropdown, { OptionInterface } from 'src/components/FilterDropdown';

import { DEBOUNCE_TIMEOUT } from 'src/constants';

interface Props {
  onRolesFilter: (rolesFilter: TrainingStatus[]) => void;
  onStatusFilter: (status: InterviewModuleMemberState[]) => void;
  onSearch: (val: string) => void;
}

const Filters: FC<Props> = ({ onRolesFilter, onStatusFilter, onSearch }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>('');

  const roleOptions: OptionInterface<TrainingStatus>[] = useMemo(
    () => [
      {
        label: t('hiring-dashboard-interviewers:interviewers.moduleMember.shadow'),
        value: TrainingStatus.Shadow,
      },
      {
        label: t('hiring-dashboard-interviewers:interviewers.moduleMember.reverseShadow'),
        value: TrainingStatus.ReverseShadow,
      },
      {
        label: t('hiring-dashboard-interviewers:interviewers.moduleMember.trained'),
        value: TrainingStatus.Trained,
      },
    ],
    [t]
  );

  const statusOptions: OptionInterface<InterviewModuleMemberState>[] = useMemo(
    () => [
      {
        label: t('hiring-dashboard-interviewers:interviewers.moduleMember.paused'),
        value: InterviewModuleMemberState.Paused,
      },
      {
        label: t('hiring-dashboard-interviewers:interviewers.moduleMember.needsApproval'),
        value: InterviewModuleMemberState.NeedsApproval,
      },
    ],
    [t]
  );

  const debouncedHandleSearch = useDebouncedCallback(onSearch, DEBOUNCE_TIMEOUT);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    debouncedHandleSearch(e.target.value);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <TextField
        size="small"
        variant="outlined"
        placeholder={t('hiring-dashboard-common:common.search.placeholder')}
        value={search}
        type="text"
        onChange={handleSearch}
        InputProps={{
          startAdornment: <SearchIcon />,
        }}
      />
      <FilterDropdown
        defaultText={t('hiring-dashboard-interviewers:interviewers.moduleLog.column.role')}
        options={roleOptions}
        onChange={onRolesFilter}
        getLabel={(values) => {
          if (values.length === 1) {
            const label = roleOptions.find((option) => option.value === values[0])?.label || '';
            return t('hiring-dashboard-interviewers:interviewers.moduleMember.roleWithValue', { value: label });
          }
          if (values.length > 1) {
            return t('hiring-dashboard-interviewers:interviewers.moduleMember.rolesCount', { count: values.length });
          }
          return t('hiring-dashboard-interviewers:interviewers.moduleLog.column.role');
        }}
      />
      <FilterDropdown
        defaultText={t('hiring-dashboard-interviewers:interviewers.moduleLog.column.status')}
        options={statusOptions}
        onChange={onStatusFilter}
        getLabel={(values) => {
          if (values.length === 1) {
            const label = statusOptions.find((option) => option.value === values[0])?.label || '';
            return t('hiring-dashboard-interviewers:interviewers.moduleMember.statusWithValue', { value: label });
          }
          if (values.length > 1) {
            return t('hiring-dashboard-interviewers:interviewers.moduleMember.statusesCount', {
              count: values.length,
            });
          }
          return t('hiring-dashboard-interviewers:interviewers.moduleLog.column.status');
        }}
      />
    </Stack>
  );
};

export default Filters;
